// ---------------------------------------------------------------------------
// Context-Aware HTML Escaping
// ---------------------------------------------------------------------------
// These are the ONLY escaping functions used by component renderers.
// Upstream sanitization (sanitizePuckData) handles input cleaning.
// These handle safe output construction.

/**
 * Escape a value for use inside an HTML attribute (href, src, alt, style, data-*).
 * Must be used inside double-quoted attributes: attr="..."
 */
export function attr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Escape a value for use as text content between HTML tags.
 */
export function text(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Escape a value for safe interpolation inside a JavaScript single-quoted string literal.
 * Used for tracking IDs embedded in <script> tags.
 * Handles: backslash, single quote, newlines, and </script> breakout.
 */
export function jsString(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/'/g, "\\'")
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/<\//g, "<\\/");
}

/**
 * Validate and escape a URL for use in href/src attributes.
 * Only allows http:, https:, mailto:, tel: schemes.
 * Relative paths must match a safe pattern.
 */
export function url(value: string, fallback = "#"): string {
  if (!value || typeof value !== "string") return attr(fallback);

  try {
    const parsed = new URL(value);
    const isAllowed = ["https:", "http:", "mailto:", "tel:"].includes(
      parsed.protocol,
    );
    return isAllowed ? attr(value) : attr(fallback);
  } catch {
    // Relative URLs — only allow safe characters
    if (/^[a-zA-Z0-9/_#?=&.\-~%+]+$/.test(value)) return attr(value);
    return attr(fallback);
  }
}

/**
 * Validate and escape an image source. In addition to the schemes accepted by
 * `url()`, this allows base64-encoded `data:image/<format>` URIs because the
 * editor's `sanitizeImageSrc` permits them and operators legitimately paste
 * them into the Image block. SVG is excluded because inline `<svg>` data URIs
 * can carry script payloads.
 */
export function imageUrl(value: string, fallback = "#"): string {
  if (!value || typeof value !== "string") return attr(fallback);
  const trimmed = value.trim();
  if (/^data:image\/(png|jpe?g|gif|webp|avif);base64,[A-Za-z0-9+/=]+$/i.test(trimmed)) {
    return attr(trimmed);
  }
  return url(trimmed, fallback);
}
